import { SettingsIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { flexRender, getCoreRowModel, OnChangeFn, SortingState, useReactTable } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { DataTablePagination } from "./pagination";
import { DataTableProps } from "@/components/data-table/types/universal-data-table-props.type";
import { CityOption, PaginationParams } from "@/modules/users/users.type";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { DropdownAutoCompleteSelect } from "@/components/form-control/dropdown-autocomplete";
import { filterArrayByString, sortAscLocale } from "helper/array";

type DataTableSearchProps<TData, TValue> = Omit<
  DataTableProps<TData, TValue>,
  "onChangePage" | "currentPage" | "sortOrders" | "onSortingChange" | "searchString" | "onSearchChange"
> & {
  currentPage: PaginationParams;
  setCurrentPage: (page: number) => void;
  searchPlaceholder?: string;
  loading?: boolean;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  hideSearch?: boolean;
  cities?: CityOption[];
  cityId?: string;
  onCityChange?: (id: string) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
};

export function DataTable<TData, TValue>({
  columns,
  data,
  loading,
  currentPage,
  setCurrentPage,
  totalPage,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  pageSize,
  onPageSizeChange,
  hideSearch,
  cities,
  cityId,
  onCityChange,
  sorting,
  onSortingChange,
}: DataTableSearchProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = useState({});
  const [citySearch, setCitySearch] = useState("");

  const sortedCityOptions = useMemo(() => {
    if (!cities) return [];
    const sorted = sortAscLocale(
      cities.map((c) => ({ value: c.id, label: c.name })),
      "label",
    );
    return [{ value: "", label: "Toutes les villes" }, ...sorted];
  }, [cities]);

  const cityOptions = citySearch ? filterArrayByString(sortedCityOptions, "label", citySearch) : sortedCityOptions;

  const showSearch = !hideSearch && onSearchChange !== undefined;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: setRowSelection,
    onSortingChange,
    state: {
      rowSelection,
      sorting,
    },
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  return (
    <div>
      <div className="flex items-center py-4">
        {showSearch && (
          <Input
            placeholder={searchPlaceholder ?? "Filtrer par nom, rôle ou ville..."}
            value={searchValue ?? ""}
            type="search"
            onChange={(event) => onSearchChange(event.target.value)}
            onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
            className="w-62.5 bg-transparent border h-8.75 px-1 border-gray-300 rounded-md focus:border-gray-500 text-sm"
          />
        )}
        {cities && onCityChange && (
          <div className="ml-2 w-44">
            <DropdownAutoCompleteSelect
              value={cityId ?? ""}
              options={cityOptions}
              onChangeValue={(val) => onCityChange(val)}
              handleSearch={setCitySearch}
              searchString={citySearch}
              placeholder="Toutes les villes"
              className="h-8.75 text-xs border-gray-300 font-normal"
            />
          </div>
        )}
        {onPageSizeChange && (
          <div className="flex items-center ml-2 gap-2">
            <span className={cn("text-xs text-muted-foreground whitespace-nowrap ", showSearch ? "ml-8" : "")}>
              Lignes par page
            </span>
            <Select
              value={String(pageSize ?? currentPage.pageSize ?? 10)}
              onValueChange={(val) => onPageSizeChange(Number(val))}
            >
              <SelectTrigger className="h-8.75 w-20 text-xs border-gray-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="15">15</SelectItem>
                <SelectItem value="20">20</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="ml-auto text-xs font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md h-9 flex items-center"
            >
              <SettingsIcon />

              <span className="hidden lg:inline">View</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) => column.toggleVisibility(!!value)}
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="rounded-lg border ">
        <Table>
          <TableHeader className="bg-muted">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} className="p-3">
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(pageSize ?? currentPage.pageSize ?? 10)].map((_, i) => (
                <TableRow key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  {columns.map((_, j) => (
                    <TableCell key={j} className="p-3">
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={`data-[state=selected]:bg-gray-200 ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="text-muted-foreground p-3 text-xs ">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="sticky bottom-0 bg-white z-10">
        <DataTablePagination currentPage={currentPage} setCurrentPage={setCurrentPage} totalPage={totalPage} />
      </div>
    </div>
  );
}
